import React, { useState, useRef, useEffect } from 'react';
import { useContainerOperations } from '../../hooks/useContainerOperations';
import { ConfirmationDialog } from '../Common/ConfirmationDialog';
import { ShowEyeIcon, HideEyeIcon } from 'assests/GmsIcons';
import type { ItemAPI } from '../../types/api.types';

// Compact three-dot icon for item menu
const MoreIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
    <circle cx="8" cy="3" r="1.5" />
    <circle cx="8" cy="8" r="1.5" />
    <circle cx="8" cy="13" r="1.5" />
  </svg>
);

// Compact delete icon
const DeleteIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3,6 5,6 21,6" />
    <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2v2" />
  </svg>
);

interface ItemActionsMenuProps {
  item: ItemAPI;
}

export const ItemActionsMenu = ({ item }: ItemActionsMenuProps) => {
  const { updateItem, removeItem } = useContainerOperations();
  const [isOpen, setIsOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const isVisible = item.configuration?.isVisible !== false;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleToggleVisibility = () => {
    updateItem(item.id, {
      configuration: {
        ...item.configuration,
        isVisible: !isVisible,
      },
    });
    setIsOpen(false);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
    setIsOpen(false);
  };

  const handleDeleteConfirm = () => {
    removeItem(item.id);
    setShowDeleteConfirm(false);
  };

  return (
    <div ref={menuRef} className="tw-relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="tw-p-0.5 tw-rounded hover:tw-bg-surface-hover tw-text-ink-tertiary hover:tw-text-ink tw-transition-colors"
        aria-label="Item actions"
        data-testid={`item-actions-${item.id}`}
      >
        <MoreIcon />
      </button>

      {isOpen && (
        <div className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-bg-surface-raised tw-rounded tw-shadow-ds-md tw-border tw-border-edge tw-py-1 tw-z-50 tw-min-w-[110px]">
          {/* Hide/Show toggle */}
          <button
            onClick={handleToggleVisibility}
            className="tw-w-full tw-px-2.5 tw-py-1.5 tw-text-left tw-text-xs tw-text-ink hover:tw-bg-surface-hover tw-flex tw-items-center tw-gap-2"
            data-testid={`item-toggle-visibility-${item.id}`}
          >
            <span className="tw-w-[12px] tw-h-[12px] tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 [&_svg]:tw-w-[12px] [&_svg]:tw-h-[12px] [&_path]:tw-stroke-current">
              {isVisible ? <HideEyeIcon /> : <ShowEyeIcon />}
            </span>
            {isVisible ? 'Hide' : 'Show'}
          </button>

          <div className="tw-border-t tw-border-edge tw-my-0.5" />

          {/* Delete */}
          <button
            onClick={handleDeleteClick}
            className="tw-w-full tw-px-2.5 tw-py-1.5 tw-text-left tw-text-xs tw-text-tone-danger-ink hover:tw-bg-tone-danger-tint tw-flex tw-items-center tw-gap-2"
            data-testid={`item-delete-${item.id}`}
          >
            <DeleteIcon />
            Delete
          </button>
        </div>
      )}

      <ConfirmationDialog
        id={`delete-item-${item.id}`}
        isOpen={showDeleteConfirm}
        title="Delete Item"
        message={`Are you sure you want to delete "${item.name || item.type}"?`}
        secondaryMessage="This action can be undone with Ctrl+Z."
        confirmText="Delete"
        confirmButtonClass="tw-bg-tone-danger-bold hover:tw-bg-tone-danger-bold-hover tw-text-ink-inverse"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};

export default ItemActionsMenu;
